<template>
    <div class="space-y-4 p-4 md:p-6">
        <!-- Welcome Section -->
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-4">
            <!-- Left: Welcome and Description -->
            <div class="lg:col-span-2">
                <h1 class="text-lg md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    Welcome back, {{ userInfo?.name || 'Admin' }} 👋
                </h1>
                <p class="text-gray-600 dark:text-gray-400 text-xs md:text-sm">
                    Your Triad music system is running smoothly. Manage your songs, playlists, and teams from this central dashboard.
                </p>
            </div>
            
            <!-- Right: Time Online -->
            <Card class="dark:!bg-gray-800 lg:col-span-2 !max-w-full">
                <template #header>
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Time Online</h3>
                    <p class="text-xs text-gray-500 dark:text-gray-400">System Uptime</p>
                </template>
                <div class="flex items-center justify-between">
                    <div>
                        <p class="text-3xl font-bold text-gray-900 dark:text-white">{{ systemUptime }}</p>
                    </div>
                    <div class="flex-shrink-0">
                        <div class="w-20 h-20 relative">
                            <svg class="transform -rotate-90" width="80" height="80">
                                <circle
                                    cx="40"
                                    cy="40"
                                    r="35"
                                    fill="none"
                                    stroke="#e5e7eb"
                                    stroke-width="8"
                                    class="dark:stroke-gray-600"
                                />
                                <circle
                                    cx="40"
                                    cy="40"
                                    r="35"
                                    fill="none"
                                    stroke="#10b981"
                                    stroke-width="8"
                                    :stroke-dasharray="circumference"
                                    :stroke-dashoffset="circumference - (uptimePercentage * circumference)"
                                    class="transition-all duration-300"
                                />
                            </svg>
                            <div class="absolute inset-0 flex items-center justify-center">
                                <span class="text-xs font-semibold text-green-600 dark:text-green-400">Online</span>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        </div>

        <!-- Statistics Cards -->
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <!-- Songs Card -->
            <StatsCard
                title="Songs"
                :value="totalSongs"
                :loading="loading"
                :icon="MusicalNoteIcon"
                trend=""
                bgColor="bg-gradient-to-br from-purple-500 to-purple-600"
            />
            
            <!-- Playlists Card -->
            <StatsCard
                title="Playlists"
                :value="totalPlaylists"
                :loading="loading"
                :icon="ClipboardDocumentListIcon"
                trend=""
                bgColor="bg-gradient-to-br from-blue-500 to-blue-600"
            />
            
            <!-- Teams Card -->
            <StatsCard
                title="Teams"
                :value="totalTeams"
                :loading="loading"
                :icon="UserGroupIcon"
                trend=""
                bgColor="bg-gradient-to-br from-orange-500 to-orange-600"
            />
            
            <!-- Schedules Card -->
            <StatsCard
                title="Schedules"
                :value="totalSchedules"
                :loading="loading"
                :icon="CalendarIcon"
                trend=""
                bgColor="bg-gradient-to-br from-teal-500 to-teal-600"
            />
            
            <!-- Realtime Messages Card -->
            <StatsCard
                title="Realtime Msgs"
                :value="realtimeMessagesCount"
                :loading="loading"
                :icon="ClockIcon"
                trend=""
                bgColor="bg-gradient-to-br from-green-500 to-green-600"
            />
            
            <!-- Active Users Card -->
            <StatsCard
                title="Active Users"
                :value="activeUsersCount"
                :loading="loading"
                :icon="UserGroupIcon"
                trend=""
                bgColor="bg-gradient-to-br from-indigo-500 to-indigo-600"
            />
        </div>

        <!-- Main Content Grid -->
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-6">
            <!-- Left Column - Biggest Playlists with Bar Chart -->
            <Card class="dark:!bg-gray-800 lg:col-span-4 !max-w-full">
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Biggest Playlists</h3>
                        <button class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                            <EllipsisVerticalIcon class="w-5 h-5" />
                        </button>
                    </div>
                </template>
                <Loading v-if="loading" />
                <div v-else class="space-y-4">
                    <!-- Bar Chart -->
                    <div class="space-y-3">
                        <div
                            v-for="(playlist, index) in biggestPlaylists"
                            :key="index"
                            class="flex items-center gap-3"
                        >
                            <div class="flex-shrink-0 w-8 text-xs text-gray-600 dark:text-gray-400">{{ index + 1 }}</div>
                            <div class="flex-1">
                                <p class="text-sm font-medium text-gray-900 dark:text-white mb-1">{{ playlist.name || 'Untitled' }}</p>
                                <div class="w-full bg-gray-200 rounded-full h-6 dark:bg-gray-700 relative overflow-hidden">
                                    <div
                                        class="h-6 rounded-full flex items-center justify-end pr-2 transition-all duration-500"
                                        :class="getBarColor(index)"
                                        :style="{ width: `${playlist.percentage}%` }"
                                    >
                                        <span class="text-xs font-semibold text-white">{{ playlist.songCount }} songs</span>
                                    </div>
                                </div>
                            </div>
                            <div class="flex-shrink-0 w-12 text-right">
                                <span class="text-sm font-semibold text-gray-900 dark:text-white">{{ playlist.percentage }}%</span>
                            </div>
                        </div>
                    </div>
                    <!-- Legend -->
                    <div class="flex flex-wrap gap-x-6 gap-y-2 text-xs">
                        <div
                            v-for="(playlist, index) in biggestPlaylists"
                            :key="index"
                            class="flex items-center gap-2"
                        >
                            <div class="w-3 h-3 rounded" :class="getBarColor(index)" />
                            <span class="text-gray-600 dark:text-gray-400">{{ playlist.name }} {{ playlist.percentage }}%</span>
                        </div>
                    </div>
                    <div v-if="biggestPlaylists.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400">
                        No playlists available
                    </div>
                </div>
            </Card>

            <!-- Right Column - Popular Songs -->
            <Card class="dark:!bg-gray-800 lg:col-span-2 !max-w-full">
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Popular Songs</h3>
                        <button class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                            <EllipsisVerticalIcon class="w-5 h-5" />
                        </button>
                    </div>
                    <div class="mt-2">
                        <div class="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 uppercase">
                            <span>Song</span>
                            <span>Access</span>
                        </div>
                    </div>
                </template>
                <Loading v-if="loading" />
                <div v-else class="space-y-3">
                    <div
                        v-for="(song, index) in popularSongs"
                        :key="index"
                        class="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        <div class="flex items-center gap-3 flex-1 min-w-0">
                            <div class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                                {{ song.title?.[0]?.toUpperCase() || '?' }}
                            </div>
                            <div class="min-w-0 flex-1">
                                <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ song.title || 'Untitled' }}</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ song.artist?.name || 'Unknown Artist' }}</p>
                            </div>
                        </div>
                        <div class="flex-shrink-0 ml-3">
                            <span class="text-sm font-semibold text-gray-900 dark:text-white">{{ song.accessCount || 0 }}</span>
                        </div>
                    </div>
                    <div v-if="popularSongs.length === 0" class="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
                        No songs available
                    </div>
                </div>
            </Card>
        </div>

        <!-- Bottom Row: Latest Users, Next Event, Cache Status -->
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-6">
            <!-- Latest Users -->
            <Card class="dark:!bg-gray-800 lg:col-span-2 !max-w-full">
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Latest Users</h3>
                        <button class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                            <EllipsisVerticalIcon class="w-5 h-5" />
                        </button>
                    </div>
                </template>
                <Loading v-if="loading" />
                <div v-else class="space-y-3">
                    <div
                        v-for="(user, index) in latestUsers"
                        :key="index"
                        class="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                        <div class="flex-shrink-0">
                            <div class="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold">
                                {{ (user.name || user.email || 'U')[0].toUpperCase() }}
                            </div>
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="text-sm font-medium text-gray-900 dark:text-white truncate">{{ user.name || user.email }}</p>
                            <p class="text-xs text-gray-500 dark:text-gray-400">
                                {{ formatDate(user.created_at) }}
                            </p>
                        </div>
                    </div>
                    <div v-if="latestUsers.length === 0" class="text-center py-4 text-gray-500 dark:text-gray-400 text-sm">
                        No users
                    </div>
                </div>
            </Card>

            <!-- Next Event -->
            <Card class="dark:!bg-gray-800 lg:col-span-2 !max-w-full">
                <template #header>
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Next Event</h3>
                </template>
                <Loading v-if="loading" />
                <div v-else-if="nextEvent" class="space-y-4">
                    <div class="bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900 dark:to-purple-800 rounded-lg p-4">
                        <div class="flex items-center gap-3 mb-3">
                            <div class="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                                <CalendarIcon class="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <p class="text-sm font-semibold text-gray-900 dark:text-white">Upcoming Webinar</p>
                            </div>
                        </div>
                        <p class="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                            {{ nextEvent.title || 'Event Title' }}
                        </p>
                        <div class="mt-4 space-y-2">
                            <div class="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                <CalendarIcon class="w-4 h-4" />
                                <span>{{ formatEventDate(nextEvent.date) }}</span>
                            </div>
                            <div class="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                <ClockIcon class="w-4 h-4" />
                                <span>{{ nextEvent.duration || 'TBD' }}</span>
                            </div>
                        </div>
                    </div>
                    <button class="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200">
                        Join The Event
                    </button>
                </div>
                <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
                    No upcoming events
                </div>
            </Card>

            <!-- Cache Status -->
            <Card class="dark:!bg-gray-800 lg:col-span-2 !max-w-full">
                <template #header>
                    <div class="flex items-center justify-between">
                        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Cache Status</h3>
                        <button class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
                            <EllipsisVerticalIcon class="w-5 h-5" />
                        </button>
                    </div>
                </template>
                <Loading v-if="loading" />
                <div v-else class="space-y-4">
                    <div class="space-y-3">
                        <div class="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                            <div class="flex items-center gap-2">
                                <div class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                                <span class="text-sm font-medium text-gray-900 dark:text-white">Status</span>
                            </div>
                            <span class="text-xs px-2 py-1 rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 font-semibold">
                                Active
                            </span>
                        </div>
                        <div class="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                            <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Cache Keys</p>
                            <p class="text-lg font-semibold text-gray-900 dark:text-white">{{ cacheKeys }}</p>
                        </div>
                        <div class="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                            <p class="text-xs text-gray-500 dark:text-gray-400 mb-1">Hit Rate</p>
                            <p class="text-lg font-semibold text-gray-900 dark:text-white">{{ cacheHitRate }}%</p>
                        </div>
                    </div>
                    <button
                        @click="resetCache"
                        class="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200"
                        :disabled="resettingCache"
                    >
                        {{ resettingCache ? 'Resetting...' : 'Reset Cache' }}
                    </button>
                </div>
            </Card>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import Card from '@/components/common/TriadCard.vue';
import Loading from '@/components/TriadLoad.vue';
import StatsCard from '@/components/StatsCard.vue';
import toastStore from '@/store/toast.store';
import { 
    MusicalNoteIcon, 
    ClipboardDocumentListIcon,
    UserGroupIcon,
    CalendarIcon,
    ClockIcon,
    EllipsisVerticalIcon
} from '@heroicons/vue/24/solid';

// Data
const loading = ref(true);
const totalSongs = ref(0);
const totalPlaylists = ref(0);
const totalTeams = ref(0);
const totalSchedules = ref(0);
const userInfo = ref(null);
const biggestPlaylists = ref([]);
const popularSongs = ref([]);
const latestUsers = ref([]);
const nextEvent = ref(null);
const cacheStats = ref({});
const systemStartTime = ref(Date.now());
const realtimeMessages = ref(0);
const authMetrics = ref({ totalUsers: 0, activeUsers: 0, newUsersToday: 0 });

const resettingCache = ref(false);

// Computed properties
const systemUptime = computed(() => {
    const uptimeMs = Date.now() - systemStartTime.value;
    const hours = Math.floor(uptimeMs / (1000 * 60 * 60));
    const minutes = Math.floor((uptimeMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
});

const uptimePercentage = computed(() => {
    // Calculate uptime as a percentage (for the circular progress)
    // Assuming 24 hours = 100%
    const uptimeMs = Date.now() - systemStartTime.value;
    const uptimeHours = uptimeMs / (1000 * 60 * 60);
    return Math.min(uptimeHours / 24, 1);
});

const circumference = computed(() => 2 * Math.PI * 35);

const cacheKeys = computed(() => {
    return cacheStats.value?.appCache?.keys || 0;
});

const cacheHitRate = computed(() => {
    const stats = cacheStats.value?.appCache;
    if (!stats || !stats.hits || !stats.misses) return 0;
    const total = stats.hits + stats.misses;
    return total > 0 ? Math.round((stats.hits / total) * 100) : 0;
});

const realtimeMessagesCount = computed(() => {
    return realtimeMessages.value.toLocaleString();
});

const activeUsersCount = computed(() => {
    return authMetrics.value.activeUsers || 0;
});

// Helper functions
const getBarColor = (index) => {
    const colors = [
        'bg-gradient-to-r from-purple-500 to-purple-600',
        'bg-gradient-to-r from-teal-500 to-teal-600',
        'bg-gradient-to-r from-green-500 to-green-600',
        'bg-gradient-to-r from-red-500 to-red-600',
        'bg-gradient-to-r from-orange-500 to-orange-600',
        'bg-gradient-to-r from-yellow-500 to-yellow-600'
    ];
    return colors[index % colors.length];
};

const resetCache = async () => {
    try {
        resettingCache.value = true;
        const response = await fetch('/api/admin/cache/reset');
        if (response.ok) {
            toastStore.dispatch('addMessage', {
                title: 'Cache Reset',
                message: 'Cache reset successfully',
                icon: 'success'
            });
        } else {
            toastStore.dispatch('addMessage', {
                title: 'Cache Reset',
                message: 'Failed to reset cache',
                icon: 'error'
            });
        }
        
        // Refresh cache stats
        await fetchCacheStats();
    } catch (error) {
        console.error('Error resetting cache:', error);
        toastStore.dispatch('addMessage', {
            title: 'Cache Reset',
            message: 'Failed to reset cache',
            icon: 'error'
        });
    } finally {
        resettingCache.value = false;
    }
};

// Individual data fetching functions
const fetchUserInfo = async () => {
    try {
        const response = await fetch('/api/user/info');
        const data = await response.json();
        userInfo.value = data;
    } catch (error) {
        console.error('Error fetching user info:', error);
    }
};

const fetchStats = async () => {
    try {
        const response = await fetch('/api/admin/dashboard/stats');
        const data = await response.json();
        totalSongs.value = data.totalSongs || 0;
        totalPlaylists.value = data.totalPlaylists || 0;
        totalTeams.value = data.totalTeams || 0;
        totalSchedules.value = data.totalSchedules || 0;
    } catch (error) {
        console.error('Error fetching stats:', error);
    }
};

const fetchBiggestPlaylists = async () => {
    try {
        const response = await fetch('/api/admin/dashboard/biggest-playlists');
        const data = await response.json();
        biggestPlaylists.value = data;
    } catch (error) {
        console.error('Error fetching biggest playlists:', error);
    }
};

const fetchPopularSongs = async () => {
    try {
        const response = await fetch('/api/admin/dashboard/popular-songs');
        const data = await response.json();
        popularSongs.value = data;
    } catch (error) {
        console.error('Error fetching popular songs:', error);
    }
};

const fetchLatestUsers = async () => {
    try {
        const response = await fetch('/api/admin/dashboard/latest-users');
        const data = await response.json();
        latestUsers.value = data;
    } catch (error) {
        console.error('Error fetching latest users:', error);
    }
};

const fetchNextEvent = async () => {
    try {
        const response = await fetch('/api/admin/dashboard/next-event');
        const data = await response.json();
        nextEvent.value = data;
    } catch (error) {
        console.error('Error fetching next event:', error);
    }
};

const fetchCacheStats = async () => {
    try {
        const response = await fetch('/api/admin/dashboard/cache-stats');
        const data = await response.json();
        cacheStats.value = data;
    } catch (error) {
        console.error('Error fetching cache stats:', error);
    }
};

const fetchRealtimeMetrics = async () => {
    try {
        const response = await fetch('/api/admin/dashboard/realtime-metrics');
        const data = await response.json();
        realtimeMessages.value = data.messageCount || 0;
    } catch (error) {
        console.error('Error fetching realtime metrics:', error);
    }
};

const fetchAuthMetrics = async () => {
    try {
        const response = await fetch('/api/admin/dashboard/auth-metrics');
        const data = await response.json();
        authMetrics.value = data;
    } catch (error) {
        console.error('Error fetching auth metrics:', error);
    }
};

// Main fetch data function
const fetchData = async () => {
    try {
        loading.value = true;
        
        // Fetch all data in parallel
        await Promise.all([
            fetchUserInfo(),
            fetchStats(),
            fetchBiggestPlaylists(),
            fetchPopularSongs(),
            fetchLatestUsers(),
            fetchNextEvent(),
            fetchCacheStats(),
            fetchRealtimeMetrics(),
            fetchAuthMetrics()
        ]);
        
        loading.value = false;
    } catch (error) {
        console.error('Error fetching dashboard data:', error);
        loading.value = false;
    }
};

// Format date helper
const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
        const d = new Date(date);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch (e) {
        return 'N/A';
    }
};

// Format event date helper
const formatEventDate = (date) => {
    if (!date) return 'TBD';
    try {
        const d = new Date(date);
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
    } catch (e) {
        return 'TBD';
    }
};

onMounted(() => {
    fetchData();
});
</script>
